import { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
// MSAL
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { authPolicies } from "../../authConfig";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useUserCardUpdate } from "../../hooks/useUserCardUpdate";
import { useCheckAPI } from "../../hooks/useCheckAPI";
// Components
import { RHFInputField } from "./RHFInputField";
// MUI
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
// import CardHeader from "@mui/material/CardHeader";
// import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

export const SettingsForm = ({ userCard }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance } = useMsal();
  const [viewMode, setViewMode] = useState(true);
  const { setAlertNotification } = useContext(AlertNotificationContext);

  let defaultValues = {
    userid: userCard.userid,
    username: userCard.username,
    name: userCard.name,
    phoneno: userCard.phoneno,
    email: userCard.email,
    defaultshiptocode: userCard.defaultshiptocode,
    entitlementreport: userCard.entitlementreport === "No" ? false : true,
    adminid: userCard.adminid,
    role: userCard.role,
    allowApprovalEmail: userCard.allowApprovalEmail === "No" ? false : true,
  };

  // Form Validation text
  const validationHelper = {
    name: { required: "Name is required" },
    phoneno: { required: "Phone number is required" },
    email: {
      required: "Email is Required",
      pattern: "Invalid Email Address",
    },
  };

  const { handleSubmit, control } = useForm({ defaultValues });

  const handleEdit = () => setViewMode(false);
  const handleCancel = () => setViewMode(true);

  const resetPassword = authPolicies.authorities.resetPassword;

  const handleChangePassword = () => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      instance.acquireTokenRedirect(resetPassword);
    }
  };

  // Update User Card Mutation
  const { mutate, isSuccess, data } = useUserCardUpdate();
  // Success Function
  const successFunc = () => {
    setAlertNotification({
      message: `Account updated`,
      severity: "info",
    });
    setViewMode(true);
  };
  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });
  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  const handleOnSubmit = (data) => {
    mutate({
      data: {
        user: {
          name: data.name,
          phoneno: data.phoneno,
          email: data.email,
          defaultshiptocode: userCard.defaultshiptocode,
          entitlementreport: data.entitlementreport,
          adminid: userCard.adminid,
          allowApprovalEmail: data.allowApprovalEmail,
        },
      },
    });
  };

  const NameInput = () => {
    return (
      <RHFInputField
        name="name"
        label="Name"
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={viewMode}
      />
    );
  };
  const PhoneInput = () => {
    return (
      <RHFInputField
        name="phoneno"
        label="Mobile No."
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={viewMode}
      />
    );
  };
  const EmailInput = () => {
    return (
      <RHFInputField
        name="email"
        label="Email"
        control={control}
        validationHelper={validationHelper}
        rules={{
          required: true,
          pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
        }}
        viewMode={viewMode}
      />
    );
  };

  const EntitlementNotificationSwitch = () => {
    return (
      <Controller
        name="entitlementreport"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            control={
              <Switch checked={value} disabled={viewMode} onChange={onChange} />
            }
            label="Entitlement Email Report"
          />
        )}
      />
    );
  };

  const ApprovalNotificationSwitch = () => {
    return (
      <Controller
        name="allowApprovalEmail"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            control={
              <Switch checked={value} disabled={viewMode} onChange={onChange} />
            }
            label="Approval Email Notification"
          />
        )}
      />
    );
  };

  // const DraftOrderNotificationSwitch = () => {
  //   return (
  //     <Controller
  //       name="entitlementreport"
  //       control={control}
  //       render={({ field: { onChange, value } }) => (
  //         <FormControlLabel
  //           control={
  //             <Switch checked={value} disabled={viewMode} onChange={onChange} />
  //           }
  //           label="Outstanding Draft Orders Email Report"
  //         />
  //       )}
  //     />
  //   );
  // };

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        maxWidth: 800,
      }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmit(handleOnSubmit)}
    >
      <Card>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ m: 2 }}>
            General Settings
          </Typography>
        </Box>
        <Divider />
        <Stack direction="column" spacing={3} sx={{ m: 2 }}>
          {/* <TextField disabled label="Username" value={userCard.username} /> */}
          {/* <TextField disabled label="Access Level" value={userCard.role} /> */}
          <NameInput />
          <PhoneInput />
          <EmailInput />
          {userCard.allowentitlement === "true" && (
            <EntitlementNotificationSwitch />
          )}
          {userCard.staffRequestApprove === "true" && (
            <ApprovalNotificationSwitch />
          )}
        </Stack>
        <Divider />
        <CardActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          {resetPassword ? (
            <Button onClick={handleChangePassword} size="sm" variant="outlined">
              Change Password
            </Button>
          ) : (
            <Box />
          )}
          <Box>
            {viewMode ? (
              <Button onClick={handleEdit} size="sm" variant="contained">
                Edit
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button onClick={handleCancel} size="sm" variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" size="sm" variant="contained">
                  Save
                </Button>
              </Stack>
            )}
          </Box>
        </CardActions>
      </Card>
    </Box>
  );
};
